import { createStore } from "vuex";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  orderBy,
  limit,
  writeBatch,
  runTransaction,
} from "firebase/firestore";
import { db, auth } from "@/firebase";
import { imagePathsRemoved, deleteInsightImage } from "@/utils/insightImages";
import {
  ruleId,
  cleanRuleText,
  emptyRulebook,
  MAX_STORED_RULES,
} from "@/utils/rulebook";
import {
  normalizeChapter,
  normalizeInsight,
  sortByOrder,
  ensureIds,
  serializeChapters,
  snapshotEntryFromRaw,
  snapshotFromDocs,
  diffChapterTrees,
  chunkArray,
  chapterPath,
  insightPath,
} from "@/utils/insightsModel";
import { signOut } from "firebase/auth";
import router from "@/router/index.js";
import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

// Recency of a changelog entry: createdAt (epoch ms) when present, else noon
// of its YYYY-MM-DD date — seeded entries lack createdAt, and midnight would
// make a same-day dismissal marker never cover them.
function changelogEntryTs(e) {
  if (e.createdAt) return Number(e.createdAt);
  const t = Date.parse((e.date || "") + "T12:00:00");
  return Number.isNaN(t) ? 0 : t;
}

// Persist the "seen up to" marker: localStorage fast-path + the user's own
// users doc (durable across devices). Never throws.
// Load the chapters/insights tree of a project: the normalised, ordered tree
// for the UI plus a snapshot of the raw docs (keyed by path) that
// updateProject diffs against, so a save only touches what changed.
async function loadChaptersTree(projectUid) {
  const snapshot = {};
  const chapSnap = await getDocs(
    collection(db, "projects", projectUid, "chapters")
  );
  const chapters = await Promise.all(
    chapSnap.docs.map(async (c) => {
      const rawChapter = { ...c.data(), uid: c.id };
      snapshot[chapterPath(c.id)] = snapshotEntryFromRaw(rawChapter, "chapter");
      const insSnap = await getDocs(
        collection(db, "projects", projectUid, "chapters", c.id, "insights")
      );
      const insights = insSnap.docs.map((d) => {
        const rawInsight = { ...d.data(), uid: d.id };
        snapshot[insightPath(c.id, d.id)] = snapshotEntryFromRaw(
          rawInsight,
          "insight"
        );
        return normalizeInsight(rawInsight, d.id);
      });
      return normalizeChapter(rawChapter, c.id, sortByOrder(insights));
    })
  );
  return { chapters: sortByOrder(chapters), snapshot };
}

// Project saves run one at a time: the chat aside auto-saves an insight while
// the Insights page may hit Save, and two concurrent diffs against the same
// snapshot would double-write or clobber each other.
let saveChain = Promise.resolve();

async function persistChangelogSeenAt(uid, ts) {
  try {
    localStorage.setItem("changelogSeenAt:" + uid, String(ts));
  } catch (e) {
    /* non-fatal */
  }
  try {
    await setDoc(
      doc(db, "users", uid),
      { changelogSeenAt: ts },
      { merge: true }
    );
  } catch (e) {
    console.warn("persistChangelogSeenAt failed:", e);
  }
}

export default createStore({
  state: {
    finetuning: null,
    chat: [],
    insight: null,
    loading: false,
    projects: [],
    project: null,
    // { [path]: canonical json } of the chapters/insights docs as loaded (or
    // last saved). Lives outside `project` so views cannot mutate it.
    chaptersSnapshot: null,
    user: null,
    users: [],
    clients: [],
    benchmarks: [],
    // Admin-managed library of conversation starters (global, shared by all
    // projects). Loaded directly from the admin/conversationStarters doc so it
    // is available to non-admin users too (getAdmin 403s for them).
    conversationStarters: [],
    // Colleague-authored Skills (best-practice SKILL.md-shaped instruction
    // packages). Loaded directly from the `skills` collection so every
    // authenticated user can read them (used by the Skills section and the
    // project-chat skill picker).
    skills: [],
    // Transient text handed from a saved note to the chat input when the user
    // clicks "Terug in chat" from another route. Consumed (and cleared) by
    // ProjectChat on mount.
    chatPrefill: null,
    // True when the signed-in user is an external guest (from the `guest`
    // custom claim). The server (rules + callables) enforces the real
    // boundaries — this only drives the UI.
    guest: false,
    // Project ids the guest may open (from their guestIndex). Empty for
    // colleagues. Drives the router fence and the /projects list.
    guestProjectIds: [],
    // Whether the guest intro wizard is open (auto on first sign-in, or
    // reopened from the Guide nav item).
    guestIntroOpen: false,
    // "What's new" dialog (colleagues only): unseen changelog entries shown
    // once after login, tracked via a changelogSeenAt timestamp on the user's
    // own users doc (with a localStorage fast-path).
    whatsNewOpen: false,
    whatsNewEntries: [],
    whatsNewTotal: 0,
    // The signed-in colleague's personal rulebook (rulebooks/{uid}): the
    // standing preferences Alice applies in their own project chat and when
    // refining an insight. Learned server-side after each answer, editable on
    // the profile page. Guests have none.
    rulebook: null,
  },
  getters: {
    isGuest: (state) => state.guest === true,
    guestProjectIds: (state) => state.guestProjectIds,
    canEditProject: (state) => !state.guest,
    canGenerateReports: (state) => !state.guest,
    canDeleteFiles: (state) => !state.guest,
  },
  mutations: {
    setChat(state, val) {
      state.chat = val;
    },
    setRulebook(state, val) {
      state.rulebook = val;
    },
    setProjects(state, val) {
      state.projects = val;
    },
    setProject(state, val) {
      state.project = val;
    },
    // Fields a callable wrote on the project doc (e.g. the Drive link from
    // linkDriveFolder): updateProject writes the doc from this object, so it
    // must hold the new values, not the ones loaded before.
    setProjectFields(state, { uid, fields }) {
      if (!state.project || state.project.uid !== uid) return;
      Object.assign(state.project, fields);
    },
    setChapters(state, val) {
      state.project.chapters = val;
    },
    setChaptersSnapshot(state, val) {
      state.chaptersSnapshot = val;
    },
    setUser(state, val) {
      state.user = val;
    },
    setLoading(state, val) {
      state.loading = val;
    },
    setUsers(state, val) {
      state.users = val;
    },
    setClients(state, val) {
      state.clients = val;
    },
    setFinetuning(state, val) {
      state.finetuning = val;
    },
    setInsight(state, val) {
      state.insight = val;
    },
    setBenchmarks(state, val) {
      state.benchmarks = val;
    },
    setGemini(state, val) {
      state.gemini = val;
    },
    setSaved(state, val) {
      if (state.project) state.project.saved = val;
    },
    setChatPrefill(state, val) {
      state.chatPrefill = val;
    },
    setConversationStarters(state, val) {
      state.conversationStarters = val;
    },
    setSkills(state, val) {
      state.skills = val;
    },
    setGuest(state, val) {
      state.guest = val;
    },
    setGuestProjectIds(state, val) {
      state.guestProjectIds = val;
    },
    setGuestIntroOpen(state, val) {
      state.guestIntroOpen = val;
    },
    setWhatsNewOpen(state, val) {
      state.whatsNewOpen = val;
    },
    setWhatsNewEntries(state, { entries, total }) {
      state.whatsNewEntries = entries;
      state.whatsNewTotal = total;
    },
  },
  actions: {
    async setUser({ commit, dispatch }, user) {
      commit("setUser", user);

      // External guests (magic-link sign-in) carry a `guest` claim. They see
      // only the projects they were invited to — the eager org-wide fetches
      // below would all be permission-denied for them.
      const { claims } = await user.getIdTokenResult();
      if (claims.guest) {
        commit("setGuest", true);
        await dispatch("fetchGuestProjects");
        dispatch("fetchConversationStarters");
        dispatch("fetchSkills");
        dispatch("maybeOpenGuestIntro");
        // Land on the projects list. GuestSignIn drives the first-run flow
        // (name prompt) and navigates itself, so don't yank a guest who is
        // mid-sign-in or already deep-linked into one of their projects.
        await router.isReady();
        const path = router.currentRoute.value.path;
        if (
          !path.startsWith("/guest") &&
          !path.startsWith("/project") &&
          path !== "/profile"
        ) {
          router.push("/projects");
        }
        return;
      }

      commit("setGuest", false);
      // Only steer to /projects after an explicit sign-in (the auth listener
      // also fires on every hard refresh to restore the session — the user
      // should stay on whatever page they refreshed, e.g. /skills).
      await router.isReady();
      if (router.currentRoute.value.path === "/signin") {
        router.push("/projects");
      }
      dispatch("fetchProjects");
      dispatch("fetchAdmin");
      dispatch("fetchUsers");
      dispatch("fetchClients");
      dispatch("fetchBenchmarks");
      dispatch("fetchConversationStarters");
      dispatch("fetchSkills");
      dispatch("fetchRulebook");
      dispatch("maybeOpenWhatsNew");
    },
    async signOut({ commit, state }) {
      const wasGuest = !!state.guest;
      signOut(auth);
      commit("setUser", null);
      commit("setGuest", null);
      router.push(wasGuest ? "/guest" : "/signin");
    },
    // Save the project doc plus the chapters/insights tree. The tree is written
    // as a diff against the snapshot taken at load (or after the last save):
    // new/changed docs are set, removed docs are deleted, ids stay stable — so
    // ordering, links and per-insight data survive a save. Legacy docs (no
    // `order`) always differ from the snapshot and get rewritten once.
    updateProject({ state, commit }) {
      const run = async () => {
        if (!state.project || !state.project.uid) return;
        commit("setLoading", true);
        try {
          const projectUid = state.project.uid;
          const p = { ...state.project };
          delete p.obslogs;
          // Saved notes live in their own subcollection (managed by the
          // addSavedNote/updateSavedNote/deleteSavedNote actions). Never write
          // the in-memory array back into the project doc here, or it would
          // shadow the subcollection and grow the doc unbounded.
          delete p.saved;
          const tree = Array.isArray(state.project.chapters)
            ? state.project.chapters
            : [];
          delete p.chapters;

          ensureIds(tree);
          const nextDocs = serializeChapters(tree);
          let prev = state.chaptersSnapshot;
          if (!prev) prev = (await loadChaptersTree(projectUid)).snapshot;
          const ops = diffChapterTrees(prev, nextDocs);
          // Firestore caps a batch at 500 operations.
          for (const chunk of chunkArray(ops, 450)) {
            const batch = writeBatch(db);
            chunk.forEach((op) => {
              const ref = doc(
                db,
                "projects",
                projectUid,
                ...op.path.split("/")
              );
              if (op.type === "set") batch.set(ref, op.data);
              else batch.delete(ref);
            });
            await batch.commit();
          }

          await updateDoc(doc(db, "projects", projectUid), p);
          // Images that left an insight (or went with a deleted insight) are
          // removed from Storage now that the doc write is final. Best effort
          // and in the background: an orphaned file is not worth failing a
          // save over.
          ops.forEach((op) => {
            if (!op.path.includes("/insights/") || !prev[op.path]) return;
            let before = null;
            try {
              before = JSON.parse(prev[op.path]);
            } catch (e) {
              return;
            }
            const after = op.type === "set" ? op.data.images : [];
            imagePathsRemoved(before && before.images, after).forEach((path) =>
              deleteInsightImage(path)
            );
          });
          // Only after every write succeeded: a failed save keeps the old
          // snapshot so the next attempt re-issues the same diff.
          commit("setChaptersSnapshot", snapshotFromDocs(nextDocs));
        } finally {
          commit("setLoading", false);
        }
      };
      const next = saveChain.then(run, run);
      saveChain = next.catch(() => {});
      return next;
    },
    // Guests can't read the whole projects collection. Their access index
    // (guestIndex/{uid}/projects, server-maintained) lists the projects they
    // may open; fetch just those docs for the /projects list.
    // Never throws — a failed/denied index read must not break sign-in. Worst
    // case the guest lands on an empty projects list.
    async fetchGuestProjects({ commit, state }) {
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid) return;
      try {
        const now = Date.now();
        const idxSnap = await getDocs(
          collection(db, "guestIndex", uid, "projects")
        );
        const ids = idxSnap.docs
          .map((d) => d.data())
          .filter((e) => {
            const exp =
              e.expiresAt && typeof e.expiresAt.toMillis === "function"
                ? e.expiresAt.toMillis()
                : Number(e.expiresAt) || 0;
            return ["invited", "active"].includes(e.status) && exp > now;
          })
          .map((e) => e.projectId);
        commit("setGuestProjectIds", ids);

        const projects = [];
        await Promise.all(
          ids.map(async (id) => {
            try {
              const snap = await getDoc(doc(db, "projects", id));
              if (snap.exists()) projects.push(snap.data());
            } catch (e) {
              /* a since-revoked project is denied by rules — skip it */
            }
          })
        );
        projects.sort((a, b) =>
          (a && a.title ? a.title : "").localeCompare(
            b && b.title ? b.title : ""
          )
        );
        commit("setProjects", projects);
      } catch (err) {
        console.warn("fetchGuestProjects failed (empty list):", err);
        commit("setGuestProjectIds", []);
        commit("setProjects", []);
      }
    },
    // Org-wide project list (colleagues only — guests can't read the whole
    // collection). Never throws: a guest may briefly reach this on a direct
    // /projects reload before their guest flag is set, and a denied read must
    // not surface as an uncaught rejection. On failure the existing store
    // (e.g. the guest's own list from fetchGuestProjects) is left untouched.
    // Open the guest intro wizard on first sign-in (once). "Seen" is stored on
    // the guest's own users doc (durable across devices), with a localStorage
    // fast-path. Never throws.
    async maybeOpenGuestIntro({ commit, state }) {
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid) return;
      try {
        if (localStorage.getItem("guestIntroSeen:" + uid) === "1") return;
      } catch (e) {
        /* localStorage unavailable — fall through to the doc check */
      }
      let seen = false;
      try {
        const snap = await getDoc(doc(db, "users", uid));
        seen = !!(snap.exists() && snap.data().guestSeenIntro);
      } catch (e) {
        /* denied/offline — treat as unseen, worst case it shows once more */
      }
      if (!seen) commit("setGuestIntroOpen", true);
    },
    // "What's new" dialog (colleagues only): open once after login when there
    // are changelog entries newer than the user's changelogSeenAt marker.
    // Without a marker, a brand-new account is silently marked as seen
    // (nothing is "new" to them), while an established account falls back to
    // its creation time as the baseline, so long-absent colleagues still see
    // what shipped while they were away. Never throws: a failure here must
    // not break sign-in.
    async maybeOpenWhatsNew({ commit, state }) {
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid || state.guest) return;
      try {
        let seenAt = 0;
        let hasMarker = false;
        try {
          const local = Number(localStorage.getItem("changelogSeenAt:" + uid));
          if (local) {
            seenAt = local;
            hasMarker = true;
          }
        } catch (e) {
          /* localStorage unavailable — fall through to the doc marker */
        }
        try {
          const snap = await getDoc(doc(db, "users", uid));
          const docVal = Number(snap.exists() && snap.data().changelogSeenAt);
          if (docVal) {
            seenAt = Math.max(seenAt, docVal);
            hasMarker = true;
          }
        } catch (e) {
          /* denied/offline — rely on the localStorage marker */
        }

        const snap = await getDocs(
          query(collection(db, "changelog"), orderBy("date", "desc"), limit(10))
        );
        const entries = snap.docs
          .map((d) => ({
            ...d.data(),
            id: d.id,
            ts: changelogEntryTs(d.data()),
          }))
          .sort((a, b) => b.ts - a.ts);
        if (!entries.length) return;

        if (!hasMarker) {
          // No marker anywhere. Colleague accounts are created on first
          // Google sign-in, so a freshly created account means a brand-new
          // user: seed the marker silently. An established account without
          // a marker predates the marker (or lost it) — baseline on the
          // account creation time so the entries they missed still show.
          const created = Date.parse(
            auth.currentUser?.metadata?.creationTime || ""
          );
          if (!created || Date.now() - created < 60 * 60 * 1000) {
            await persistChangelogSeenAt(
              uid,
              Math.max(Date.now(), entries[0].ts)
            );
            return;
          }
          seenAt = created;
        }

        const unseen = entries.filter((e) => e.ts > seenAt);
        if (!unseen.length) return;
        commit("setWhatsNewEntries", {
          entries: unseen.slice(0, 5),
          total: unseen.length,
        });
        commit("setWhatsNewOpen", true);
      } catch (e) {
        console.warn("maybeOpenWhatsNew failed:", e);
      }
    },
    // Close the dialog and advance the seen marker past everything shown.
    async markWhatsNewSeen({ commit, state }) {
      commit("setWhatsNewOpen", false);
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid) return;
      const ts = state.whatsNewEntries.reduce(
        (max, e) => Math.max(max, e.ts || 0),
        Date.now()
      );
      await persistChangelogSeenAt(uid, ts);
    },
    // Close the wizard and remember it was seen.
    async markGuestIntroSeen({ commit, state }) {
      commit("setGuestIntroOpen", false);
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid) return;
      try {
        localStorage.setItem("guestIntroSeen:" + uid, "1");
      } catch (e) {
        /* non-fatal */
      }
      try {
        await setDoc(
          doc(db, "users", uid),
          { guestSeenIntro: true },
          { merge: true }
        );
      } catch (e) {
        console.warn("markGuestIntroSeen failed:", e);
      }
    },
    async fetchProjects({ commit }) {
      let projects = [];
      try {
        const q = query(collection(db, "projects"));
        const querySnapshot = await getDocs(q);
        await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            let r = doc.data();
            // Capture Firestore server-side createTime so the projects list
            // has a real "created" date until docs carry an explicit createdAt.
            if (typeof doc.createTime === "function") {
              try {
                r._firestoreCreateTime = doc.createTime();
              } catch (e) {
                /* createTime not available offline — leave undefined */
              }
            }
            projects.push(r);
          })
        );
        projects.sort((a, b) => {
          const aTitle = a && a.title ? a.title : "";
          const bTitle = b && b.title ? b.title : "";
          return aTitle.localeCompare(bTitle);
        });
        commit("setProjects", projects);
      } catch (err) {
        console.warn("fetchProjects failed (leaving current list):", err);
      }
    },
    async fetchAdmin({ commit }) {
      const getAdmin = httpsCallable(functions, "getAdmin");
      const res = await getAdmin();
      console.log(res.data);
      commit("setFinetuning", res.data.finetuning);
      commit("setGemini", res.data.gemini);
    },
    // Read the global conversation-starter library directly from Firestore so
    // it works for every authenticated user (getAdmin would 403 non-admins).
    async fetchConversationStarters({ commit }) {
      const snap = await getDoc(doc(db, "admin", "conversationStarters"));
      commit(
        "setConversationStarters",
        snap.exists() ? snap.data().items || [] : []
      );
    },
    // Read the Skills library directly from Firestore so it works for every
    // authenticated user (mirrors fetchConversationStarters above). Guests may
    // only read PUBLISHED skills (rules), so their query is filtered to those;
    // never throws so a denied read can't break sign-in.
    async fetchSkills({ commit, getters }) {
      try {
        const base = collection(db, "skills");
        const q = getters.isGuest
          ? query(base, where("status", "==", "published"))
          : query(base);
        const querySnapshot = await getDocs(q);
        const skills = querySnapshot.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));
        skills.sort((a, b) =>
          (a.title || a.name || "").localeCompare(b.title || b.name || "")
        );
        commit("setSkills", skills);
      } catch (err) {
        console.warn("fetchSkills failed (leaving current list):", err);
      }
    },
    async fetchUsers({ commit }) {
      let users = [];

      const q = query(collection(db, "users"));
      const querySnapshot = await getDocs(q);
      await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          let r = doc.data();
          users.push(r);
        })
      );

      commit("setUsers", users);
    },
    // Colleagues only — guests can't read the clients collection. Never throws
    // (see fetchProjects) so a stray guest call degrades to no clients.
    async fetchClients({ commit }) {
      try {
        let clients = [];
        const q = query(collection(db, "clients"));
        const querySnapshot = await getDocs(q);
        querySnapshot.docs.forEach((doc) => {
          clients.push({ ...doc.data(), uid: doc.id });
        });
        clients.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        commit("setClients", clients);
      } catch (err) {
        console.warn("fetchClients failed (leaving current list):", err);
      }
    },
    // Create a client and return its new id, then refresh the cached list.
    // Idempotent: if a client with the same name (case-insensitive) already
    // exists, return its id instead of creating a duplicate.
    async createClient({ dispatch, state }, { name, color = null }) {
      const trimmed = (name || "").trim();
      const existing = state.clients.find(
        (c) => (c.name || "").toLowerCase() === trimmed.toLowerCase()
      );
      if (existing) return existing.uid;
      const ref = await addDoc(collection(db, "clients"), {
        name: trimmed,
        color,
        status: "active",
        allowed_emails: [],
        allowed_uids: [],
        createdAt: Math.floor(Date.now() / 1000),
        createdBy: state.user?.uid || null,
      });
      await updateDoc(doc(db, "clients", ref.id), { uid: ref.id });
      await dispatch("fetchClients");
      return ref.id;
    },
    async fetchInsights({ commit }) {
      commit("setChapters", []);
    },
    async fetchBenchmarks({ commit }) {
      let benchmarks = [];
      const q = query(collection(db, "sales", "benchmarks", "neuro-ux"));
      const querySnapshot = await getDocs(q);

      querySnapshot.docs.map(async (doc) => {
        let r = doc.data();
        r.companies = [];
        r.uid = doc.id;
        const companiesReq = query(
          collection(db, "sales", "benchmarks", "neuro-ux", doc.id, "companies")
        );
        const companiesSnap = await getDocs(companiesReq);
        companiesSnap.docs.map((c) => r.companies.push(c.data()));

        benchmarks.push(r);
      });

      commit("setBenchmarks", benchmarks);
    },
    async fetchProject({ commit }, uid) {
      commit("setLoading", true);
      const docRef = doc(db, "projects", uid);
      const docSnap = await getDoc(docRef);
      let r = docSnap.data();
      r.obslogs = [];
      r.files = [];

      r.recordings = [];

      const recordingReq = query(
        collection(db, "projects", r.uid, "recordings")
      );
      const recordingSnap = await getDocs(recordingReq);
      recordingSnap.docs.map((rec) =>
        r.recordings.push({ ...rec.data(), id: rec.id })
      );

      const obq = query(collection(db, "projects", r.uid, "obslog"));
      const obslogSnapshot = await getDocs(obq);
      obslogSnapshot.docs.map(async (o) => {
        let obslog = o.data();
        obslog.input = [];
        const inputRef = query(
          collection(db, "projects", r.uid, "obslog", o.id, "input")
        );
        const inputSnap = await getDocs(inputRef);
        inputSnap.docs.map((i) => {
          obslog.input.push(i.data());
        });
        r.obslogs.push(obslog);
      });

      // Chapters & Insights — ordered tree for the UI plus the snapshot the
      // diff save in updateProject compares against.
      const tree = await loadChaptersTree(r.uid);
      r.chapters = tree.chapters;
      commit("setChaptersSnapshot", tree.snapshot);

      const fileReq = query(collection(db, "projects", r.uid, "files"));
      const fileSnap = await getDocs(fileReq);
      fileSnap.docs.map((f) => r.files.push({ ...f.data(), id: f.id }));

      // Saved notes (lightweight snippets bookmarked from chat answers).
      r.saved = [];
      const savedReq = query(collection(db, "projects", r.uid, "saved"));
      const savedSnap = await getDocs(savedReq);
      savedSnap.docs.map((s) => r.saved.push(s.data()));
      r.saved.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      commit("setProject", r);
      commit("setLoading", false);
    },
    // ---- Rulebook (per-user preferences) -------------------------------
    async fetchRulebook({ commit, state, getters }) {
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid || getters.isGuest) return;
      try {
        const snap = await getDoc(doc(db, "rulebooks", uid));
        commit(
          "setRulebook",
          snap.exists()
            ? { ...emptyRulebook(uid), ...snap.data() }
            : emptyRulebook(uid)
        );
      } catch (e) {
        console.warn("fetchRulebook failed:", e);
      }
    },
    // Every client edit runs in a transaction: the server's learn pass writes
    // the same doc after each answer, and neither side may lose the other's
    // change. `mutate` receives a deep-enough copy and returns the next doc.
    async mutateRulebook({ commit, state, getters }, mutate) {
      const uid = state.user?.uid || auth.currentUser?.uid;
      if (!uid || getters.isGuest) return null;
      try {
        const ref = doc(db, "rulebooks", uid);
        const next = await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          const current = snap.exists()
            ? { ...emptyRulebook(uid), ...snap.data() }
            : emptyRulebook(uid);
          const draft = {
            ...current,
            uid,
            rules: (current.rules || []).map((r) => ({ ...r })),
          };
          const updated = (await mutate(draft)) || draft;
          updated.uid = uid;
          updated.updatedAt = Date.now();
          tx.set(ref, updated);
          return updated;
        });
        commit("setRulebook", next);
        return next;
      } catch (e) {
        console.warn("rulebook update failed:", e);
        return null;
      }
    },
    setRulebookEnabled({ dispatch }, enabled) {
      return dispatch("mutateRulebook", (rb) => ({
        ...rb,
        enabled: !!enabled,
      }));
    },
    async addRule({ dispatch }, { text, category }) {
      const clean = cleanRuleText(text);
      if (clean.length < 8) return null;
      const id = await ruleId(clean);
      return dispatch("mutateRulebook", (rb) => {
        const now = Date.now();
        const existing = rb.rules.find((r) => r.id === id);
        if (existing) {
          Object.assign(existing, {
            text: clean,
            status: "active",
            source: "user",
            confidence: 1,
            updatedAt: now,
          });
          return rb;
        }
        // Keep under the rules cap: drop the oldest inactive non-user entry.
        while (rb.rules.length >= MAX_STORED_RULES) {
          const idx = rb.rules
            .map((r, i) => ({ r, i }))
            .filter(({ r }) => r.status !== "active" && r.source !== "user")
            .sort((a, b) => (a.r.updatedAt || 0) - (b.r.updatedAt || 0))[0];
          if (!idx) break;
          rb.rules.splice(idx.i, 1);
        }
        rb.rules.push({
          id,
          text: clean,
          category: category || "style",
          status: "active",
          source: "user",
          confidence: 1,
          hits: 0,
          evidence: [],
          createdAt: now,
          updatedAt: now,
        });
        return rb;
      });
    },
    updateRuleText({ dispatch }, { id, text }) {
      const clean = cleanRuleText(text);
      if (clean.length < 8) return null;
      return dispatch("mutateRulebook", (rb) => {
        const r = rb.rules.find((x) => x.id === id);
        if (r) {
          r.text = clean;
          r.source = "user";
          r.updatedAt = Date.now();
        }
        return rb;
      });
    },
    setRuleStatus({ dispatch }, { id, status }) {
      return dispatch("mutateRulebook", (rb) => {
        const r = rb.rules.find((x) => x.id === id);
        if (r) {
          r.status = status;
          r.updatedAt = Date.now();
        }
        return rb;
      });
    },
    dismissRule({ dispatch }, id) {
      return dispatch("setRuleStatus", { id, status: "dismissed" });
    },
    dismissLearnedRules({ dispatch }) {
      return dispatch("mutateRulebook", (rb) => {
        const now = Date.now();
        rb.rules.forEach((r) => {
          if (r.source === "learned" && r.status !== "dismissed") {
            r.status = "dismissed";
            r.updatedAt = now;
          }
        });
        return rb;
      });
    },

    async addSavedNote({ state, commit }, payload) {
      const p = state.project;
      if (!p) return null;
      const note = {
        text: payload.text || "",
        label: payload.label || "",
        note: payload.note || null,
        source: payload.source || null,
        createdAt: Date.now(),
        createdBy: state.user?.uid || null,
      };
      const ref = await addDoc(
        collection(db, "projects", p.uid, "saved"),
        note
      );
      await updateDoc(doc(db, "projects", p.uid, "saved", ref.id), {
        uid: ref.id,
      });
      const saved = [{ ...note, uid: ref.id }, ...(p.saved || [])];
      commit("setSaved", saved);
      return ref.id;
    },
    async updateSavedNote({ state, commit }, payload) {
      const p = state.project;
      if (!p || !payload.uid) return;
      const patch = {
        text: payload.text || "",
        label: payload.label || "",
        note: payload.note || null,
      };
      await updateDoc(doc(db, "projects", p.uid, "saved", payload.uid), patch);
      const saved = (p.saved || []).map((n) =>
        n.uid === payload.uid ? { ...n, ...patch } : n
      );
      commit("setSaved", saved);
    },
    async deleteSavedNote({ state, commit }, uid) {
      const p = state.project;
      if (!p || !uid) return;
      await deleteDoc(doc(db, "projects", p.uid, "saved", uid));
      const saved = (p.saved || []).filter((n) => n.uid !== uid);
      commit("setSaved", saved);
    },
  },
  modules: {},
});
